/**
 * inputLayerWarnings — Input_Layer_Consolidation IC6.
 *
 * Pure helper that surfaces non-blocking warnings whenever a
 * lower-precedence input layer disagrees with the resolved value.
 *
 * An explicit 250 MW campus IT-demand claim can disagree with a 300 MW
 * owner IT-demand target. Generation, individual equipment and phase
 * quantities are different concepts and must not become that warning.
 * The owner intent remains authoritative; warnings never rewrite it.
 *
 * Engineering rules (CLAUDE.md §5):
 *   - Pure. No I/O, no React. Safe to call from anywhere.
 *   - Returns an empty array when nothing disagrees so callers can
 *     spread it without null-checking.
 *   - Compares using `Object.is` for primitives; numbers within 0.01%
 *     are treated as equal to avoid floating-point noise.
 */
import type { SiteContext } from '../types/siteContext';
import { INPUT_PRECEDENCE, resolveField, type InputFieldId, type InputLayer, type InputSources } from './inputResolution';
import { effectivePower, effectiveTimeline } from './projectIntent';
import { normaliseCooling } from '../types/cooling';

export interface LayerDisagreement {
  /** INPUT_PRECEDENCE field id this disagreement is about. */
  fieldId: InputFieldId;
  /** The layer that won the resolver — the value the report will see. */
  resolvedLayer: InputLayer;
  /** The resolved value (formatted as a display string). */
  resolvedDisplay: string;
  /** The losing layer's name. */
  losingLayer: InputLayer;
  /** The losing layer's value, formatted as a display string. */
  losingDisplay: string;
  /** Human-readable concept label (e.g. "Project size (MW)"). */
  conceptLabel: string;
  /** Severity — info means "informational", warn means "user should reconcile". */
  severity: 'info' | 'warn';
}

/* ── Per-field comparators ───────────────────────────────────────── */

interface LayerValue {
  layer: InputLayer;
  value: unknown;
  display: string;
}

function format(v: unknown): string {
  if (v == null) return '(not set)';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(2);
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return String(v);
}

function eqLoose(a: unknown, b: unknown): boolean {
  if (a == null || b == null) return false;
  if (typeof a === 'number' && typeof b === 'number') {
    if (a === 0 && b === 0) return true;
    return Math.abs(a - b) / Math.max(Math.abs(a), Math.abs(b)) < 0.0001;
  }
  if (typeof a === 'string' && typeof b === 'string') return a.trim().toLowerCase() === b.trim().toLowerCase();
  return Object.is(a, b);
}

/** Collect each layer's value for a given fieldId (skips null/undefined). */
function collectLayerValues(fieldId: InputFieldId, sources: InputSources): LayerValue[] {
  const out: LayerValue[] = [];
  const spec = INPUT_PRECEDENCE[fieldId];
  if (!spec) return out;
  for (const layer of spec.layers) {
    const v = readLayerValue(fieldId, layer, sources);
    if (v != null) out.push({ layer, value: v, display: format(v) });
  }
  return out;
}

function readLayerValue(fieldId: InputFieldId, layer: InputLayer, sources: InputSources): unknown {
  switch (fieldId) {
    case 'projectMW': {
      if (layer === 'projectIntent') return effectivePower(sources.projectIntent ?? undefined).mw;
      // SSOT — reportPrefs.feasibility.projectSizeMW and
      // reportPrefs['power-availability'].powerRequirementMW are deleted;
      // the only remaining source layers are projectIntent and siteContext.
      if (layer === 'siteContext') {
        return comparableContextDemandMw(sources);
      }
      return undefined;
    }
    case 'inServiceDate': {
      if (layer === 'projectIntent') return effectiveTimeline(sources.projectIntent ?? undefined).inServiceDate;
      // SSOT — reportPrefs.feasibility.inServiceDate deleted;
      // PowerAvailability.timeToPowerCriticalDate is a distinct concept
      // (when power must be available) and is NOT a substitute for the
      // site-canonical in-service date — it stays on its own form but no
      // longer aliases into this resolver field.
      if (layer === 'siteContext') return claimValue(sources.siteContext, 'schedule.inservice');
      return undefined;
    }
    case 'cooling': {
      if (layer === 'projectIntent') {
        const v = sources.projectIntent?.coolingStrategy;
        if (v && v !== 'undecided') return normaliseCooling(v);
        return undefined;
      }
      // SSOT — reportPrefs.feasibility.coolingPreference and
      // reportPrefs['water-resource'].coolingType are deleted; cooling
      // resolves from projectIntent.coolingStrategy with siteContext as
      // soft fallback.
      if (layer === 'siteContext') {
        const v = claimValue(sources.siteContext, 'water.cooling')
          ?? claimValue(sources.siteContext, 'technology.cooling');
        if (typeof v === 'string') return normaliseCooling(v);
        return undefined;
      }
      return undefined;
    }
    default: {
      // For other fields, lean on the resolver's per-layer reader by
      // building a single-layer source bag and seeing what comes back.
      const partial: InputSources = {};
      if (layer === 'projectIntent') partial.projectIntent = sources.projectIntent ?? null;
      else if (layer === 'reportPrefs') partial.reportPrefs = sources.reportPrefs ?? null;
      else if (layer === 'siteContext') partial.siteContext = sources.siteContext ?? null;
      else if (layer === 'sed') partial.sed = sources.sed ?? null;
      const r = resolveField(fieldId, partial);
      return r.value ?? undefined;
    }
  }
}

/** A BTM claim can describe a turbine, phase, voltage or generation output.
 * Only compare an explicit demand quantity with the same declared boundary.
 * This is a UI warning filter; it never rewrites intent or source evidence. */
function comparableContextDemandMw(sources: InputSources): number | undefined {
  const basis = sources.projectIntent?.powerBasis;
  if (basis !== 'it-load' && basis !== 'facility') return undefined;
  for (const claim of sources.siteContext?.claims ?? []) {
    if (claim.category !== 'power.btm' || typeof claim.claimedValue === 'boolean') continue;
    const value = typeof claim.claimedValue === 'number'
      ? claim.claimedValue
      : typeof claim.claimedValue === 'string' && claim.claimedValue.trim()
        ? Number(claim.claimedValue) : NaN;
    if (!Number.isFinite(value) || value <= 0) continue;
    const text = claim.claim.trim() || claim.sourceSpan.trim();
    // Phase-specific, equipment and generation quantities are not site demand.
    if (/\b(?:phase|stage|tranche|initial|first|incremental|additional|addition|generator|generation|turbine|genset|microgrid|output)\b/i.test(text)
      || /\bper\s+(?:unit|block|module)\b/i.test(text)) continue;
    const sameBoundary = basis === 'it-load'
      ? /\b(?:IT[ -]+(?:load|demand)|critical[ -]+(?:IT[ -]+)?load)\b/i.test(text)
      : /\b(?:facility[ -]+(?:load|demand)|total[ -]+(?:site|campus|grid)[ -]+(?:load|demand))\b/i.test(text);
    if (!sameBoundary) continue;
    const quantities = [...text.matchAll(/\b(\d[\d,]*(?:\.\d+)?)\s*(GW|MW)\b/gi)];
    if (quantities.length !== 1) continue;
    const quantity = quantities[0]!;
    const native = Number(quantity[1]!.replace(/,/g, ''));
    const mw = native * (quantity[2]!.toUpperCase() === 'GW' ? 1000 : 1);
    if (mw > 0 && Number.isFinite(mw) && (eqLoose(value, native) || eqLoose(value, mw))) return mw;
  }
  return undefined;
}

function claimValue(ctx: SiteContext | null | undefined, category: string): unknown {
  if (!ctx?.claims) return undefined;
  const c = ctx.claims.find(x => x.category === category && x.claimedValue !== undefined);
  return c?.claimedValue;
}

/* ── Public API ──────────────────────────────────────────────────── */

/**
 * Walk every INPUT_PRECEDENCE field and emit a `LayerDisagreement`
 * for any lower-precedence layer that disagrees with the resolved
 * value. Empty when every layer agrees (or only one layer is set).
 */
export function detectLayerDisagreements(sources: InputSources): LayerDisagreement[] {
  const out: LayerDisagreement[] = [];
  for (const fieldId of Object.keys(INPUT_PRECEDENCE) as InputFieldId[]) {
    const layerVals = collectLayerValues(fieldId, sources);
    if (layerVals.length < 2) continue;
    const [winner, ...rest] = layerVals;
    for (const losing of rest) {
      if (!eqLoose(winner.value, losing.value)) {
        out.push({
          fieldId,
          resolvedLayer: winner.layer,
          resolvedDisplay: winner.display,
          losingLayer: losing.layer,
          losingDisplay: losing.display,
          conceptLabel: INPUT_PRECEDENCE[fieldId].conceptLabel,
          severity: INPUT_PRECEDENCE[fieldId].affects === 'score' ? 'warn' : 'info',
        });
      }
    }
  }
  return out;
}

/** Look up disagreements for a single field — convenience for inline UI. */
export function disagreementsForField(
  fieldId: InputFieldId,
  sources: InputSources,
): LayerDisagreement[] {
  return detectLayerDisagreements(sources).filter(d => d.fieldId === fieldId);
}
